import { createContext, useContext, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

const PageHeaderTargetContext = createContext<HTMLElement | null | undefined>(
  undefined,
)

export function PageHeaderTargetProvider({
  target,
  children,
}: {
  target: HTMLElement | null
  children: ReactNode
}): JSX.Element {
  return (
    <PageHeaderTargetContext.Provider value={target}>
      {children}
    </PageHeaderTargetContext.Provider>
  )
}

function Title({
  title,
  count,
  meta,
  compact,
}: {
  title: ReactNode
  count?: number
  meta?: ReactNode
  compact: boolean
}): JSX.Element {
  return (
    <div
      className="flex min-w-0 items-center gap-2"
    >
      <h1
        className={`min-w-0 truncate font-display font-semibold text-text-primary ${compact ? 'text-body' : 'text-page-title'}`}
      >
        {title}
      </h1>
      {count !== undefined && (
        <span
          title="Entries matching the current filters"
          className="shrink-0 rounded bg-surface-app px-2 py-0.5 text-body font-normal tabular-nums text-text-secondary"
        >
          {count}
        </span>
      )}
      {meta}
    </div>
  )
}

function HeaderContent({
  title,
  count,
  meta,
  action,
  compact,
}: {
  title: ReactNode
  count?: number
  meta?: ReactNode
  action?: ReactNode
  compact: boolean
}): JSX.Element {
  return (
    <div className="flex min-w-0 items-center justify-between gap-4">
      <div className="min-w-0">
        <Title title={title} count={count} meta={meta} compact={compact} />
      </div>
      {action !== undefined && <div className="shrink-0">{action}</div>}
    </div>
  )
}

/**
 * One page-title contract with two render locations:
 *
 * - inside AdminShell, the title/count/actions are portalled into the sticky
 *   top bar and the description stays with the page body;
 * - in isolated page tests or stories, the same component renders a normal
 *   inline header, so pages do not require shell scaffolding to be useful.
 */
export function PageHeader({
  title,
  count,
  meta,
  action,
}: {
  title: ReactNode
  count?: number
  meta?: ReactNode
  action?: ReactNode
}): JSX.Element {
  const target = useContext(PageHeaderTargetContext)

  if (target === undefined) {
    return (
      <header className="mb-6 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <Title title={title} count={count} meta={meta} compact={false} />
        </div>
        {action !== undefined && <div className="shrink-0">{action}</div>}
      </header>
    )
  }

  return (
    <>
      {target !== null &&
        createPortal(
          <HeaderContent
            title={title}
            count={count}
            meta={meta}
            action={action}
            compact
          />,
          target,
        )}
    </>
  )
}
